import { useEffect, useId, useRef, useState } from 'react';

export default function MapSyncStatus({ provider, connectionStatus, isNetworkSynced, isLoggedIn, recoveryNotice, recoveredFromDevice = false, readOnly = false, isDarkMode = false, buttonStyle }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const container = useRef(null);
  const detailId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      if (!container.current?.contains(event.target)) { setOpen(false); setPinned(false); }
    };
    const escape = event => {
      if (event.key === 'Escape') { setOpen(false); setPinned(false); }
    };
    document.addEventListener('pointerdown', closeOutside, true);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside, true);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  useEffect(() => {
    setPending(provider?.unsyncedChanges || 0);
    if (!provider) return undefined;
    const update = ({ number }) => setPending(number);
    provider.on('unsyncedChanges', update);
    return () => provider.off('unsyncedChanges', update);
  }, [provider]);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  let label;
  let detail;
  let attention = false;
  if (!isLoggedIn) {
    label = 'Not saved to an account';
    detail = 'Changes stay in this tab. Sign in to save your map.';
  } else if (recoveryNotice) {
    label = 'Changes need review';
    detail = 'Review the recovery message before closing this map.';
    attention = true;
  } else if (!online || connectionStatus === 'disconnected') {
    label = online ? 'Connection lost' : 'Offline';
    detail = readOnly ? 'Reconnect to see the latest changes.' : `${recoveredFromDevice ? 'Recovered from this device. ' : ''}Waiting to sync. Keep this tab open until you reconnect.`;
    attention = true;
  } else if (connectionStatus !== 'connected' || !isNetworkSynced) {
    label = 'Connecting…';
    detail = 'Waiting for the map to sync with the server.';
  } else if (pending > 0) {
    label = 'Syncing changes…';
    detail = 'Waiting for the server to acknowledge your changes. Keep this tab open.';
  } else {
    label = readOnly ? 'View only · up to date' : 'Up to date';
    detail = 'Connected. The server has acknowledged all changes sent from this tab.';
  }
  const busy = isLoggedIn && !attention && (connectionStatus !== 'connected' || !isNetworkSynced || pending > 0);
  return (
    <div ref={container} className={`mind-map-sync-status${isDarkMode ? ' mind-map-sync-status--dark' : ''}`}
      data-export-ignore="true"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => { if (!pinned) setOpen(false); }}>
      <button type="button" className={`mind-map-sync-status__button${attention || !isLoggedIn ? ' mind-map-sync-status__button--attention' : ''}`}
        style={{ ...buttonStyle, width: 30, height: 30 }}
        aria-label={`Map sync status: ${label}`} aria-expanded={open}
        aria-describedby={open ? detailId : undefined}
        onFocus={() => setOpen(true)} onBlur={() => { setOpen(false); setPinned(false); }}
        onClick={() => { setPinned(!pinned); setOpen(!pinned); }}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          className={busy ? 'mind-map-sync-status__busy' : undefined}>
          {busy ? <path d="M20 7a9 9 0 0 0-15-2L2 8m0-5v5h5M4 17a9 9 0 0 0 15 2l3-3m0 5v-5h-5" />
            : attention ? <><path d="M12 3 2 21h20L12 3Z" /><path d="M12 9v5m0 3v.1" /></>
              : !isLoggedIn ? <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M10 17h4" /></>
                : <><path d="M18 18a4 4 0 0 0 0-8 6 6 0 0 0-11.7-1A4.5 4.5 0 0 0 6.5 18" /><path d="m9 16 3 3 5-6" /></>}
        </svg>
      </button>
      <span className="mind-map-sync-status__announcement" role="status" aria-label="Map sync status" aria-live="polite" aria-atomic="true">{label}</span>
      {open && <div id={detailId} role="tooltip" className="mind-map-sync-status__popover">
        <strong>{label}</strong>
        <span className="mind-map-sync-status__detail">{detail}</span>
      </div>}
    </div>
  );
}
